import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet } from 'react-native';
import BarcodeCameraView from '../../components/camera/BarcodeCameraView';
import { Alert } from '../../components/other/Alert';
import { connect } from 'react-redux';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import Toast from 'react-native-root-toast';

import { app as appActions } from '../../actions';
import { Dialog } from '../../components';
import Text from '../../components/text/Text';
import { ProductSelector } from '../../components/selector';
import PickerButton from '../../components/picker/PickerButton/PickerButton';
import { EmptyState, PrimaryButton, Screen, SecondaryButton } from '../../design/components';
import useBarcodeScanner from '../../hooks/useBarcodeScanner';
import { useThemedStyles } from '../../hooks/useThemedStyles';

const SET_PRODUCT_BARCODE = gql`
	mutation setProductBarcode (
		$productId: ID,
		$barcode: String,
	) {
		setProductBarcode (
			productId: $productId,
			barcode: $barcode,
		) {
			status
		}
	}
`;

const BarcodeProductDialog = ({
	data,
	loading,
	onSubmit,
	closeDialog,
}) => {
	const styles = useThemedStyles(createStyles);
	const [product, setProduct] = useState(null);

	return (
		<View style={styles.dialogContent}>
			<Text style={styles.dialogTitle}>Barcode: {data}</Text>
			<ProductSelector
				initialValue={product}
				onChange={setProduct}
				customButton={({ onPress, label }) => {
					return (
						<PickerButton onPress={onPress}>
							{label}
						</PickerButton>
					);
				}}
			/>
			<View style={styles.dialogButtonRow}>
				<PrimaryButton
					style={styles.dialogButton}
					size="compact"
					label="OK"
					loading={loading}
					onPress={() => onSubmit(product, data, closeDialog)}
					disabled={!data || !product}
				/>
				<SecondaryButton
					style={styles.dialogButton}
					label="Scan again"
					onPress={() => { closeDialog(); }}
				/>
			</View>
		</View>
	);
};

const BarcodeScannerView = ({
	openDialog,
}) => {
	const styles = useThemedStyles(createStyles);
	const [barcode, setBarcode] = useState(null);

	const [scanProductBarcodeMutation, { loading }] = useMutation(SET_PRODUCT_BARCODE);

	const addNewBarcode = useCallback(async (product, data) => {
		try {
			await scanProductBarcodeMutation({
				variables: {
					barcode: data,
					productId: product.id,
				},
			});
			Toast.show('Barcode successfully set');
		}
		catch (e) {
			Toast.show('Error when setting barcode');
		}
	}, [scanProductBarcodeMutation]);

	const onSubmit = useCallback((product, data, closeDialog) => {

		if (product.barcodes?.length) {
			Alert.alert(
				'Barcode already exists',
				'The product already has a barcode assigned. Do you want to add another one?',
				[
					{
						text: 'Ok',
						onPress: () => {
							addNewBarcode(product, data);
							closeDialog();
						},
					},
					{
						text: 'cancel', onPress: () => console.log('cancel pressed'),
					},
				],
				{ cancelable: false },
			);
		}
		else {
			addNewBarcode(product, data);
			closeDialog();
		}
	}, [addNewBarcode]);

	const handleScannedValue = useCallback((data) => {
		setBarcode(data);

		openDialog(({ closeDialog }) => (
			<BarcodeProductDialog
				data={data}
				loading={loading}
				onSubmit={onSubmit}
				closeDialog={closeDialog}
			/>
		));
	}, [loading, onSubmit, openDialog]);

	const {
		cameraPermission,
		requestCameraPermission,
		handleBarcodeScanned,
		resetScanner,
	} = useBarcodeScanner({
		onScanned: handleScannedValue,
	});

	useEffect(() => {
		requestCameraPermission();
	}, [requestCameraPermission]);

	const onCloseDialog = () => {
		setBarcode(null);
		resetScanner();
	};

	const renderBody = () => {
		if (!cameraPermission) {
			return (
				<View style={styles.centered}>
					<Text style={styles.statusText}>Requesting camera permission</Text>
				</View>
			);
		}

		if (!cameraPermission.granted) {
			return (
				<View style={styles.centered}>
					<EmptyState
						icon="camera-off"
						title="No access to camera"
						actionLabel="Grant permission"
						onAction={requestCameraPermission}
					/>
				</View>
			);
		}

		return (
			<View style={styles.cameraArea}>
				{
					!barcode && (
						<BarcodeCameraView
							onBarcodeScanned={handleBarcodeScanned}
							style={StyleSheet.absoluteFillObject}
						/>
					)
				}
				<Dialog onClose={onCloseDialog} />
			</View>
		);
	};

	return (
		<Screen
			title="Product scanner"
			backLabel="Warehouse"
			scroll={false}
			contentContainerStyle={styles.body}
		>
			{renderBody()}
		</Screen>
	);
};

const mapDispatchToProps = dispatch => ({
	openDialog: component => dispatch(appActions.openDialog(component)),
});

export default connect(null, mapDispatchToProps)(BarcodeScannerView);

const createStyles = ({ ui: { colors, type, space } }) => ({
	body: {
		maxWidth: '100%',
	},
	cameraArea: {
		flex: 1,
		flexDirection: 'column',
		justifyContent: 'flex-end',
		backgroundColor: colors.canvas,
	},
	centered: {
		flex: 1,
		alignItems: 'center',
		justifyContent: 'center',
		padding: space.xl,
	},
	statusText: {
		...type.bodySmall,
		color: colors.textSecondary,
	},
	dialogContent: {
		paddingTop: space.lg,
		gap: space.md,
	},
	dialogTitle: {
		...type.rowTitleSemibold,
		color: colors.text,
		paddingRight: space.xl,
	},
	dialogButtonRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.sm,
	},
	dialogButton: {
		flex: 1,
	},
});
